import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Input,
  InputLabel,
  InputAdornment,
  IconButton,
  Popper,
  Paper,
  FormHelperText,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  GridFilterOperator,
  GridFilterInputValueProps,
  getGridDateOperators,
} from '@mui/x-data-grid-premium';
import { parseDateShortcut, formatDateOnly } from '@/lib/dateShortcuts';

interface DateShortcutFilterInputProps extends GridFilterInputValueProps {
  showTime?: boolean;
}

let nextId = 0;

/**
 * Converts a display value (YYYY-MM-DD) to the model value MUI's filter expects.
 * For dateTime columns the filter regex requires YYYY-MM-DDTHH:mm.
 */
function toModelValue(displayValue: string, showTime?: boolean): string {
  if (!displayValue) return '';
  if (showTime && /^\d{4}-\d{2}-\d{2}$/.test(displayValue)) {
    return `${displayValue}T00:00`;
  }
  return displayValue;
}

/**
 * Strips the THH:mm suffix from a model value so the user sees a clean date.
 */
function toDisplayValue(modelValue: string): string {
  if (!modelValue) return '';
  return modelValue.replace(/T\d{2}:\d{2}$/, '');
}

/**
 * Custom DataGridPremium filter input that supports F&O-style date shortcuts
 * with an optional calendar picker dropdown.
 *
 * Uses Input + InputLabel directly (not TextField) to avoid double-nesting
 * FormControls — the parent FilterFormValueInput is already a FormControl.
 * This keeps vertical alignment consistent with the Columns and Operator fields.
 */
function DateShortcutFilterInput(props: DateShortcutFilterInputProps) {
  const { item, applyValue, focusElementRef, showTime } = props;

  const [inputValue, setInputValue] = useState<string>(toDisplayValue(item.value ?? ''));
  const [parseError, setParseError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const popperContentRef = useRef<HTMLDivElement>(null);
  const [inputId] = useState(() => `date-shortcut-filter-${nextId++}`);

  // Wire the DataGridPremium's focusElementRef to our input
  useEffect(() => {
    if (typeof focusElementRef === 'function') {
      focusElementRef(inputRef.current);
    } else if (focusElementRef && typeof focusElementRef === 'object') {
      (focusElementRef as React.MutableRefObject<HTMLInputElement | null>).current =
        inputRef.current;
    }
  }, [focusElementRef]);

  // Sync local state when the filter item value changes externally
  useEffect(() => {
    setInputValue(toDisplayValue(item.value ?? ''));
  }, [item.value]);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        rootRef.current && !rootRef.current.contains(target) &&
        popperContentRef.current && !popperContentRef.current.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const applyDate = useCallback(
    (displayDate: string) => {
      setParseError(null);
      setInputValue(displayDate);
      applyValue({ ...item, value: toModelValue(displayDate, showTime) });
    },
    [applyValue, item, showTime],
  );

  const commitValue = useCallback(() => {
    const trimmed = inputValue.trim();

    if (!trimmed) {
      setParseError(null);
      applyValue({ ...item, value: '' });
      return;
    }

    // If already a valid YYYY-MM-DD value, apply directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      applyDate(trimmed);
      return;
    }

    const result = parseDateShortcut(trimmed);

    if (result.success && result.value) {
      applyDate(formatDateOnly(result.value.date));
    } else {
      setParseError(result.error || 'Invalid date');
    }
  }, [inputValue, applyValue, applyDate, item]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setParseError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      commitValue();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    const related = e.relatedTarget as Node;
    if (related && (
      rootRef.current?.contains(related) ||
      popperContentRef.current?.contains(related)
    )) {
      return;
    }
    commitValue();
  };

  const handleCalendarClick = () => {
    setPickerOpen((prev) => !prev);
  };

  const handlePickerChange = (value: Dayjs | null) => {
    if (value && value.isValid()) {
      applyDate(value.format('YYYY-MM-DD'));
      setPickerOpen(false);
    }
  };

  const pickerValue = inputValue ? dayjs(inputValue) : dayjs();
  const validPickerValue = pickerValue.isValid() ? pickerValue : dayjs();

  return (
    <>
      <InputLabel shrink htmlFor={inputId}>
        Value
      </InputLabel>
      <Input
        ref={rootRef}
        id={inputId}
        fullWidth
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        inputRef={inputRef}
        placeholder="e.g. t, +5, 0314"
        error={!!parseError}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={handleCalendarClick}
              size="small"
              edge="end"
            >
              <CalendarTodayIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />
      {parseError && (
        <FormHelperText error>{parseError}</FormHelperText>
      )}
      <Popper
        open={pickerOpen}
        anchorEl={rootRef.current}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
      >
        <Paper ref={popperContentRef} elevation={8}>
          <StaticDatePicker
            value={validPickerValue}
            onChange={handlePickerChange}
            displayStaticWrapperAs="desktop"
          />
        </Paper>
      </Popper>
    </>
  );
}

/**
 * Returns MUI DataGridPremium date filter operators with shortcut-enabled input.
 *
 * Wraps the built-in operators from `getGridDateOperators()` and replaces
 * the native date input with `DateShortcutFilterInput` for any operator
 * that requires a value (i.e., not isEmpty / isNotEmpty).
 */
export function getDateShortcutFilterOperators(
  showTime?: boolean,
): GridFilterOperator[] {
  const builtIn = getGridDateOperators(showTime);

  return builtIn.map((op) => {
    if (op.requiresFilterValue === false) {
      return op;
    }
    return {
      ...op,
      InputComponent: DateShortcutFilterInput,
      InputComponentProps: { showTime },
    };
  }) as GridFilterOperator[];
}
